import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from '../../components/LoadingSpinner';
import { getUserProfile } from "../../Redux/userSlice";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { FaArrowLeft } from "react-icons/fa";
import useInstagramInsightsSync from "../hooks/useInstagramInsightsSync";

const InstagramInsights = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: user, loading } = useSelector((state) => state.user);
  const { syncing, syncInstagramInsights } = useInstagramInsightsSync();

  useEffect(() => {
    dispatch(getUserProfile());
  }, [dispatch]);

  const refreshInsights = async () => {
    const result = await syncInstagramInsights();
    if (!result.success) {
      alert(result.message);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading Instagram insights..." />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">
            No profile data available
          </h2>
          <button
            onClick={() => navigate("/influencer/dashboard/influencer-profile")}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Go to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate("/influencer/dashboard/influencer-profile")}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <FaArrowLeft />
            Back to Profile
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Instagram Insights
          </h1>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {/* Refresh Button */}
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800">
              Latest 10 Posts & Reels
            </h2>
            <button
              onClick={refreshInsights}
              disabled={syncing}
              className={`px-4 py-2 text-white rounded-lg transition ${syncing ? "bg-gray-400" : "bg-orange-500 hover:bg-orange-600"
                }`}
            >
              {syncing ? "⏳ Syncing..." : "🔄 Refresh Insights"}
            </button>
          </div>

          {/* Instagram Insights Section */}
          {user.instagramInsights && user.instagramInsights.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {user.instagramInsights.map((item, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg shadow-md bg-white hover:shadow-lg transition"
                >
                  <p className="font-semibold">
                    {item.type === "REEL" ? "🎥 Reel" : "📸 Photo"}
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    {item.caption?.slice(0, 50) || "No caption provided"}
                  </p>

                  <div className="mt-3 text-sm text-gray-700 space-y-1">
                    <p>Reach: <strong>{item.insights?.reach || "-"}</strong></p>
                    <p>Likes: <strong>{item.insights?.likes || "-"}</strong></p>
                    <p>Comments: <strong>{item.insights?.comments || "-"}</strong></p>
                    <p>Shares: <strong>{item.insights?.shares || "-"}</strong></p>
                    <p>Saves: <strong>{item.insights?.saves || "-"}</strong></p>
                  </div>

                  <a
                    href={item.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 mt-3 inline-block underline"
                  >
                    View on Instagram ↗
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                ⏳ No insights found yet. They are still being collected...
              </p>
              <button
                onClick={refreshInsights}
                disabled={syncing}
                className={`px-4 py-2 text-white rounded-lg transition ${syncing ? "bg-gray-400" : "bg-orange-500 hover:bg-orange-600"
                  }`}
              >
                {syncing ? "⏳ Syncing..." : "🔄 Fetch Insights Now"}
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default InstagramInsights;

